<template>
  <!-- 보도자료 편집 모달 -->
  <a-modal
    :visible="visible"
    :footer="null"
    :mask-closable="false"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">
        {{ isNew ? '보도자료 추가' : '보도자료 편집' }}
      </div>
    </template>

    <div class="modal-content">
      <!-- 썸네일 이미지 -->
      <div class="press-card-editor__field">
        <label class="press-card-editor__label">썸네일 이미지</label>
        <image-uploader
          :model-value="localArticle.thumbnail"
          label=""
          :max-size-m-b="5"
          @update:model-value="(url) => (localArticle.thumbnail = url)"
        />
      </div>

      <!-- 언론사명 -->
      <div class="press-card-editor__field">
        <label class="press-card-editor__label">언론사 <span class="required">*</span></label>
        <a-input
          v-model:value="localArticle.mediaOutlet"
          placeholder="언론사명을 입력하세요"
        />
      </div>

      <!-- 기사 제목 (최대 60자) -->
      <div class="press-card-editor__field">
        <label class="press-card-editor__label">제목 <span class="required">*</span></label>
        <a-input
          v-model:value="localArticle.title"
          :maxlength="60"
          show-count
          placeholder="기사 제목 (최대 60자)"
        />
      </div>

      <!-- 설명 -->
      <div class="press-card-editor__field">
        <label class="press-card-editor__label">설명</label>
        <a-textarea
          v-model:value="localArticle.description"
          :rows="3"
          placeholder="기사 요약 설명"
        />
      </div>

      <!-- 기사 링크 URL -->
      <div class="press-card-editor__field">
        <label class="press-card-editor__label">링크 URL <span class="required">*</span></label>
        <a-input
          v-model:value="localArticle.linkUrl"
          type="url"
          placeholder="https://..."
        />
      </div>

      <!-- 발행일 -->
      <div class="press-card-editor__field">
        <label class="press-card-editor__label">발행일</label>
        <a-date-picker
          v-model:value="localArticle.publishedAt"
          style="width: 100%"
          placeholder="발행일을 선택하세요"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
        />
      </div>

      <!-- 모달 푸터 -->
      <div class="modal-footer">
        <!-- 기존 기사일 때 삭제 버튼 표시 -->
        <a-button
          v-if="!isNew"
          danger
          :loading="deleting"
          @click="handleDelete"
        >
          삭제
        </a-button>
        <div style="flex: 1" />
        <a-button @click="handleCancel">취소</a-button>
        <a-button
          type="primary"
          class="yellow-button"
          :loading="saving"
          @click="handleSave"
        >
          저장
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message, Modal } from 'ant-design-vue';
import ImageUploader from '../common/ImageUploader.vue';
import { HomepageApi } from '../../api';

export default {
  name: 'PressCardEditor',

  components: {
    ImageUploader,
  },

  props: {
    // 모달 표시 여부
    visible: {
      type: Boolean,
      default: false,
    },
    // 편집할 기사 데이터 (null이면 신규 등록)
    article: {
      type: Object,
      default: null,
    },
    // 기사 인덱스
    articleIndex: {
      type: Number,
      default: -1,
    },
  },

  emits: ['save', 'cancel', 'delete'],

  data() {
    return {
      // 로컬 편집 데이터
      localArticle: {
        thumbnail: '',
        mediaOutlet: '',
        title: '',
        description: '',
        linkUrl: '',
        publishedAt: null,
      },
      saving: false,
      deleting: false,
    };
  },

  computed: {
    // 신규 등록 여부 (article prop이 null인 경우)
    isNew() {
      return this.article === null;
    },
  },

  watch: {
    visible(val) {
      if (val) {
        // 모달 열릴 때 article prop으로 초기화
        this.localArticle = {
          thumbnail: this.article?.thumbnail || '',
          mediaOutlet: this.article?.mediaOutlet || '',
          title: this.article?.title || '',
          description: this.article?.description || '',
          linkUrl: this.article?.linkUrl || '',
          publishedAt: this.article?.publishedAt || null,
        };
        this.saving = false;
        this.deleting = false;
      }
    },
  },

  methods: {
    // 저장 처리: 유효성 검사 후 API 호출
    async handleSave() {
      // 필수 필드 유효성 검사
      if (!this.localArticle.mediaOutlet || this.localArticle.mediaOutlet.trim() === '') {
        message.warning('언론사명을 입력해주세요.');
        return;
      }
      if (!this.localArticle.title || this.localArticle.title.trim() === '') {
        message.warning('기사 제목을 입력해주세요.');
        return;
      }
      if (!this.localArticle.linkUrl || this.localArticle.linkUrl.trim() === '') {
        message.warning('기사 링크 URL을 입력해주세요.');
        return;
      }

      this.saving = true;
      try {
        let savedArticle;
        if (this.isNew) {
          // 신규 등록
          const res = await HomepageApi.createPressArticle(this.localArticle);
          savedArticle = res?.data || this.localArticle;
        } else {
          // 기존 기사 수정
          const res = await HomepageApi.updatePressArticle(
            this.article.id || this.article._id,
            this.localArticle
          );
          savedArticle = res?.data || { ...this.article, ...this.localArticle };
        }
        this.$emit('save', { article: savedArticle, articleIndex: this.articleIndex });
      } catch (error) {
        message.error(error.message || '저장 중 오류가 발생했습니다.');
      } finally {
        this.saving = false;
      }
    },

    // 삭제 처리: 확인 모달 후 API 호출
    handleDelete() {
      Modal.confirm({
        title: '보도자료를 삭제하시겠습니까?',
        content: '삭제된 보도자료는 복구할 수 없습니다.',
        okText: '삭제',
        okType: 'danger',
        cancelText: '취소',
        onOk: async () => {
          this.deleting = true;
          try {
            await HomepageApi.deletePressArticle(
              this.article.id || this.article._id
            );
            this.$emit('delete', { articleIndex: this.articleIndex });
          } catch (error) {
            message.error(error.message || '삭제 중 오류가 발생했습니다.');
          } finally {
            this.deleting = false;
          }
        },
      });
    },

    // 취소 처리
    handleCancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped>
.press-card-editor__field {
  margin-bottom: 16px;
}

.press-card-editor__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.required {
  color: #ff4d4f;
}
</style>
